const SORT_OPTIONS = [
  { value: 'default', label: 'Recommended' },
  { value: 'latest', label: 'Latest' },
  { value: 'price-low-high', label: 'Price: Low to High' },
  { value: 'price-high-low', label: 'Price: High to Low' },
  { value: 'rating', label: 'Highest Rated' }
]

const normalizeText = (value) => String(value ?? '').trim()

const slugify = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const capitalize = (value) => {
  const text = normalizeText(value)
  if (!text) {
    return ''
  }
  return `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`
}

export const toPocketValue = (pocket) => {
  const normalized = normalizeText(pocket).toLowerCase()

  if (!normalized) {
    return 'pocket-other'
  }

  if (normalized.startsWith('pocket-')) {
    return normalized
  }

  const stripped = normalized.replace(/^pocket\s*/i, '').replace(/\s+/g, '-')
  return `pocket-${stripped}`
}

export const toPocketLabel = (property) => {
  const pocketCode = normalizeText(property?.location?.pocket).toUpperCase()
  const area = normalizeText(property?.location?.area)

  if (pocketCode && area) {
    return `Pocket ${pocketCode} ${area}`
  }

  if (pocketCode) {
    return `Pocket ${pocketCode}`
  }

  if (area) {
    return area
  }

  return 'Other Locations'
}

export const toCityLocalityValue = (city) => {
  const citySlug = slugify(city)
  return citySlug ? `city-${citySlug}` : ''
}

export const toLocalityValue = (property) => {
  const cityValue = toCityLocalityValue(property?.location?.city)
  if (cityValue) {
    return cityValue
  }
  return toPocketValue(property?.location?.pocket)
}

export const toLocalityLabel = (property) => {
  const city = normalizeText(property?.location?.city)
  if (city) {
    return city
  }
  return toPocketLabel(property)
}

export const getLocalityCandidates = (property) => {
  const candidates = [toLocalityValue(property), toPocketValue(property?.location?.pocket)]
  return [...new Set(candidates.filter(Boolean))]
}

export const matchesLocalityFilter = (property, localityFilters = []) => {
  if (!Array.isArray(localityFilters) || localityFilters.length === 0) {
    return true
  }

  const normalizedFilterSet = new Set(localityFilters.map((value) => normalizeText(value).toLowerCase()))
  const candidates = getLocalityCandidates(property).map((value) => normalizeText(value).toLowerCase())
  return candidates.some((value) => normalizedFilterSet.has(value))
}

export const getPropertySearchText = (property) => {
  const location = property?.location ?? {}
  return [
    property?.name,
    property?.slug,
    location.pocket,
    location.area,
    location.city,
    location.address,
    property?.gender,
    property?.propertyType
  ]
    .map((value) => normalizeText(value).toLowerCase())
    .filter(Boolean)
    .join(' ')
}

export const buildCatalogMeta = (properties = []) => {
  const list = Array.isArray(properties) ? properties : []
  const pocketsMap = new Map()
  const localitiesMap = new Map()
  const gendersMap = new Map()

  let minPrice = Number.POSITIVE_INFINITY
  let maxPrice = Number.NEGATIVE_INFINITY

  list.forEach((property) => {
    const pocketValue = toPocketValue(property?.location?.pocket)
    const pocketCode = normalizeText(property?.location?.pocket).toUpperCase() || 'OTHER'
    const pocketLabel = toPocketLabel(property)
    const slug = normalizeText(property?.slug)
    const name = normalizeText(property?.name) || 'Unnamed Property'
    const monthly = Number(property?.pricing?.monthly)

    if (!pocketsMap.has(pocketValue)) {
      pocketsMap.set(pocketValue, {
        id: pocketValue,
        key: pocketCode,
        value: pocketValue,
        name: pocketLabel,
        properties: []
      })
    }

    pocketsMap.get(pocketValue).properties.push({
      id: property?.id ?? slug ?? name,
      name,
      slug
    })

    const localityValue = toLocalityValue(property)
    if (localityValue && !localitiesMap.has(localityValue)) {
      localitiesMap.set(localityValue, {
        value: localityValue,
        label: toLocalityLabel(property),
        count: 0
      })
    }

    if (localityValue) {
      localitiesMap.get(localityValue).count += 1
    }

    const genderValue = normalizeText(property?.gender).toLowerCase()
    if (genderValue && !gendersMap.has(genderValue)) {
      gendersMap.set(genderValue, {
        value: genderValue,
        label: capitalize(genderValue)
      })
    }

    if (Number.isFinite(monthly)) {
      minPrice = Math.min(minPrice, monthly)
      maxPrice = Math.max(maxPrice, monthly)
    }
  })

  const pockets = [...pocketsMap.values()]
    .map((pocket) => ({
      ...pocket,
      properties: pocket.properties
        .filter((property) => property.slug)
        .sort((a, b) => a.name.localeCompare(b.name))
    }))
    .filter((pocket) => pocket.properties.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name))

  const localities = [...localitiesMap.values()]
    .filter((locality) => locality.count > 0)
    .sort((a, b) => a.label.localeCompare(b.label))

  const genders = [...gendersMap.values()].sort((a, b) => a.label.localeCompare(b.label))

  if (!Number.isFinite(minPrice) || !Number.isFinite(maxPrice)) {
    minPrice = 0
    maxPrice = 20000
  }

  return {
    pockets,
    localities,
    genders,
    sortOptions: SORT_OPTIONS,
    priceRange: {
      min: minPrice,
      max: maxPrice
    },
    availableSlugs: list.map((property) => normalizeText(property?.slug)).filter(Boolean)
  }
}

export { SORT_OPTIONS }
